import { useEffect, useState, useContext, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import Card from "@mui/material/Card";
import Pagination from "@mui/material/Pagination";
import Icon from "@mui/material/Icon";
import TableSortLabel from "@mui/material/TableSortLabel";
import CircularProgress from "@mui/material/CircularProgress";
import Swal from "sweetalert2";
import * as XLSX from "xlsx";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

import { LanguageContext } from "App";
import SessionsApi from "../../../Classes/API/Administration/SessionsApi";
import AccountingApi from "Classes/API/AccountingApi";
import SearchAccSessionReq from "../../../Classes/Models/Requests/AccountingSessions/CreateAccountingSessionReq";
import SearchStringOrIntReq3 from "../../../Classes/Models/Requests/SearchStringOrIntReq";
import DataTable from "examples/Tables/DataTable";
import SearchSingleSessions from "./SearchSingleSessions";
import AddmissionButton from "../AddmissionButton";
import PaymentButton from "../PaymentButton";
import AccountingEntryPopup from "../AccountingEntryPopup";
import { useTenant } from "../../../context/TenantContext";
import { formatDate } from "utils/dateUtils";

const SortAccSessionsKey = {
  Id: 0,

  SessionName: 1,
  PatientName: 2,
  TherapistName: 3,
  AppointmentTime: 4,
  TimeStamp: 5,
  OriginalPrice: 6,
  FinalPrice: 7,
  IsPaidFor: 8,
  IsAbsent: 9,
  Discount: 10,
};

const SortAccSessionsDirection = {
  Ascending: 0,
  Descending: 1,
};

const SingleSessionsTable = ({
  CurrentUser,
  setCreatedNewSession,
  CreatedNewSession,
}) => {
  const navigate = useNavigate();
  const { translations } = useContext(LanguageContext);

  const { features } = useTenant();
  const isTrackerEnabled = features.find(
    (f) => f.name === "Doctor_Session_Tracker",
  )?.status;

  const [SearchReq, setSearchReq] = useState(new SearchAccSessionReq());
  const [TotalAccountingItems, setTotalAccountingItems] = useState(0);
  const [ResponseData, setResponseData] = useState([]);
  const [PaginationPages, setPaginationPages] = useState(0);
  const [Page, setPage] = useState(1);
  const [IsLoaded, setIsLoaded] = useState(false);
  const [IsSearching, setIsSearching] = useState(false);
  const [TableDataRows, setTableDataRows] = useState([]);
  const [showPaymentPopup, setShowPaymentPopup] = useState(false);
  const [selectedSession, setSelectedSession] = useState(null);

  const [SortConfig, setSortConfig] = useState({
    key: null,
    direction: SortAccSessionsDirection.Descending,
  });

  // Helper functions from original code
  const createSortableHeader = (sortKey, translationKey) => (
    <TableSortLabel
      active={SortConfig.key === sortKey}
      direction={
        SortConfig.direction === SortAccSessionsDirection.Ascending
          ? "asc"
          : "desc"
      }
      onClick={() => handleSort(sortKey)}
    >
      {translations[translationKey]}
      {SortConfig.key === sortKey && (
        <Icon fontSize="small" sx={{ ml: 0.5 }}>
          {SortConfig.direction === SortAccSessionsDirection.Ascending
            ? "arrow_upward"
            : "arrow_downward"}
        </Icon>
      )}
    </TableSortLabel>
  );
  const calculateDuration = (admission, end) => {
    if (!admission || !end) return "-";

    const start = new Date(admission);
    const finish = new Date(end);
    const diffInMs = finish - start;

    if (diffInMs < 0) return "Invalid";

    const totalMinutes = Math.ceil(diffInMs / (1000 * 60));
    const hours = Math.floor(totalMinutes / 60);
    const mins = totalMinutes % 60;

    return hours > 0 ? `${hours}h ${mins}m` : `${mins} mins`;
  };

  const createTypographyCell = (content) => (
    <MDTypography
      component="p"
      variant="button"
      color="text"
      fontWeight="medium"
    >
      {content}
    </MDTypography>
  );

  // Main Data Fetching Logic (Fixed for Search)
  const GetTableData = useCallback(
    async (passedParams = null) => {
      setIsSearching(true);
      try {
        // Priority to passedParams (from Search button) then fall back to State
        const baseParams = passedParams || SearchReq;

        const searchRequest = {
          ...baseParams,
          pagingParams: {
            pageSize: 5,
            pageNumber: Page,
          },
          SortKey: SortConfig.key,
          SortDirection: SortConfig.direction,
        };

        const res = await SessionsApi.SearchAccountingSessions(searchRequest);
        if (res.status === "Success") {
          setResponseData(res.data.list);
          setTotalAccountingItems(res.data.totalItems);
          setPaginationPages(res.data.totalPages);
          setIsLoaded(true);
        }
      } catch (error) {
        console.error("Error fetching table data:", error);
        Swal.fire("Error", "Failed to fetch data", "error");
      } finally {
        setIsSearching(false);
      }
    },
    [SearchReq, Page, SortConfig],
  );

  const GetTherapistSessions = async () => {
    const req = new SearchStringOrIntReq3();
    req.SearchInt = CurrentUser.getTherapist.id;
    const res = await SessionsApi.GetTherapistAccountingSessions(req);
    if (res.status === "Success") {
      setResponseData(res.data.list);
      setIsLoaded(true);
    }
  };

  const handleRefresh = () => {
    setIsLoaded(false);
    setCreatedNewSession(true);
    GetTableData();
  };

  const handleSort = (key) => {
    const newDirection =
      SortConfig.key === key &&
      SortConfig.direction === SortAccSessionsDirection.Ascending
        ? SortAccSessionsDirection.Descending
        : SortAccSessionsDirection.Ascending;

    setSortConfig({ key, direction: newDirection });
    setPage(1);
  };

  const ClearSort = () => {
    setSortConfig({ key: 0, direction: SortAccSessionsDirection.Descending });
    setPage(1);
  };

  const handlePaymentButtonClick = (session) => {
    if (session.isAdmitted && session.isPaidFor) {
      Swal.fire({
        title: "Error",
        text: "You cannot refund an admitted patient.",
        icon: "error",
        confirmButtonText: "Ok",
      });
      return;
    }
    if (!session.isAdmitted && !session.isPaidFor && session.isException) {
      Swal.fire({
        title: "If you want to add the payment, please remove the exception. ",
        text: "You cannot Pay for unadmitted Exception session. Remove Exception status first.",
        icon: "error",
        confirmButtonText: "Ok",
      });
      return;
    }
    setSelectedSession(session);
    setShowPaymentPopup(true);
  };

  const DeleteSession = async (id) => {
    if (!["Admin", "Owner", "HeadAccounting"].includes(CurrentUser.userType)) {
      Swal.fire({
        title: "Sorry, you do not have this permission.",
        text: "Visit your admin to request this permission.",
        icon: "error",
        confirmButtonText: "Ok",
      });
      return;
    }

    Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
    }).then(async (result) => {
      if (result.value) {
        const delReq = new SearchStringOrIntReq3();
        delReq.SearchInt = id;
        const res = await SessionsApi.DeleteAccountingSession(delReq);
        if (res.status === "Success") {
          Swal.fire("Deleted!", "Session has been deleted.", "success");
          handleRefresh();
        } else {
          Swal.fire("Failed!", res.message, "error");
        }
      }
    });
  };

  const MakeTableRow = (session) => {
    const row = {
      Id: createTypographyCell(session.id),
      Session: createTypographyCell(session.sessionName),
      Patient: createTypographyCell(session.patientName),
      Therapist: createTypographyCell(session.therapistName),
      Payment: (
        <PaymentButton
          AccSession={session}
          onClick={() => handlePaymentButtonClick(session)}
        />
      ),
      Addmission: (
        <AddmissionButton
          AccSession={session}
          setIsLoaded={setIsLoaded}
          onRefresh={handleRefresh}
        />
      ),
      AppointmentTime: createTypographyCell(
        formatDate(session.appointmentTime) === "N/A"
          ? "No Appointment Time"
          : formatDate(session.appointmentTime),
      ),
      OriginalPrice: createTypographyCell(session.originalPrice),
      Discount: createTypographyCell(`${Number(session.discount).toFixed(1)}%`),
      FinalPrice: createTypographyCell(session.finalPrice),
      Edit: (
        <MDTypography
          onClick={() =>
            navigate("/AccountingSessions/Details", {
              state: { SearchSession: session },
            })
          }
          component="a"
          variant="button"
          color="text"
          fontWeight="medium"
          sx={{ cursor: "pointer" }}
        >
          <Icon>more_vert</Icon>
        </MDTypography>
      ),
      Delete: (
        <MDButton
          onClick={() => DeleteSession(session.id)}
          variant="gradient"
          color="dark"
        >
          <Icon sx={{ fontWeight: "bold" }}>delete</Icon>
        </MDButton>
      ),
    };

    if (isTrackerEnabled) {
      row.Duration = createTypographyCell(
        calculateDuration(session.admissionTime, session.endTime),
      );
    }

    return row;
  };

  // Effects
  useEffect(() => {
    if (!IsLoaded || CreatedNewSession) {
      GetTableData();
      setCreatedNewSession(false);
    }
  }, [IsLoaded, CreatedNewSession, GetTableData]);

  useEffect(() => {
    if (IsLoaded) {
      GetTableData();
    }
  }, [Page, SortConfig]);

  useEffect(() => {
    if (ResponseData.length > 0) {
      setTableDataRows(ResponseData.map((session) => MakeTableRow(session)));
    } else {
      setTableDataRows([]);
    }
  }, [ResponseData]);

  const TableColumns = [
    {
      Header: createSortableHeader(
        SortAccSessionsKey.Id,
        "createAccountsessionSingleSessionSearchTableAccSessionID",
      ),
      accessor: "Id",
      align: "left",
    },
    {
      Header: createSortableHeader(
        SortAccSessionsKey.SessionName,
        "createAccountsessionSingleSessionSearchTableSession",
      ),
      accessor: "Session",
      align: "left",
    },
    {
      Header: createSortableHeader(
        SortAccSessionsKey.PatientName,
        "createAccountsessionSingleSessionSearchTablePatient",
      ),
      accessor: "Patient",
      align: "left",
    },
    {
      Header: createSortableHeader(
        SortAccSessionsKey.TherapistName,
        "createAccountsessionSingleSessionSearchTableTherapist",
      ),
      accessor: "Therapist",
      align: "left",
    },
    ...(["Admin", "Owner", "HeadAccounting"].includes(CurrentUser.userType) ||
    CurrentUser.userType === "AccountingEmployee" ||
    CurrentUser.userType === "SuperAdmin"
      ? [
          {
            Header:
              translations.createAccountsessionSingleSessionSearchTablePayment,
            accessor: "Payment",
            align: "left",
          },
        ]
      : []),
    {
      Header:
        translations.createAccountsessionSingleSessionSearchTableAddmission,
      accessor: "Addmission",
      align: "left",
    },
    ...(isTrackerEnabled
      ? [
          {
            Header:
              translations.createAccountsessionSingleSessionSearchTableDuration,
            accessor: "Duration",
            align: "left",
          },
        ]
      : []),
    {
      Header: createSortableHeader(
        SortAccSessionsKey.AppointmentTime,
        "createAccountsessionSingleSessionSearchTableAppointmentTime",
      ),
      accessor: "AppointmentTime",
      align: "left",
    },
    {
      Header: createSortableHeader(
        SortAccSessionsKey.OriginalPrice,
        "createAccountsessionSingleSessionSearchTableOriginalPrice",
      ),
      accessor: "OriginalPrice",
      align: "left",
    },
    {
      Header: createSortableHeader(
        SortAccSessionsKey.Discount,
        "createAccountsessionSingleSessionSearchTableDiscount",
      ),
      accessor: "Discount",
      align: "left",
    },
    {
      Header:
        translations.createAccountsessionSingleSessionSearchTableFinalPrice,
      accessor: "FinalPrice",
      align: "left",
    },
    {
      Header: translations.createAccountsessionSingleSessionSearchTableDetails,
      accessor: "Edit",
      align: "center",
    },
    {
      Header: translations.createAccountsessionSingleSessionSearchTableDelete,
      accessor: "Delete",
      align: "center",
    },
  ];

  const ExportExcelList = async () => {
    Swal.fire({
      title: "Preparing Export",
      html: `<div><p>Fetching data...</p></div>`,
      icon: "info",
      didOpen: () => Swal.showLoading(),
    });

    try {
      const exportReq = {
        ...SearchReq,
        SortKey: SortConfig.key,
        SortDirection: SortConfig.direction,
      };

      const response =
        await AccountingApi.GetAccountingSessionsForExport(exportReq);

      if (response.status === "Success" && response.data?.length > 0) {
        const isAppointmentEmployee =
          CurrentUser?.userType === "AppointmentEmployee";
        const filteredData = response.data.map((session) => {
          const formattedSession = {
            ...session,
            appointmentTime: formatDate(session.appointmentTime),
            admissionTime: formatDate(session.admissionTime),
            endTime: formatDate(session.endTime),
            timeStamp: formatDate(session.timeStamp),
          };

          if (isAppointmentEmployee) {
            const { originalPrice, discount, finalPrice, ...allowedData } =
              formattedSession;

            return allowedData;
          }
          return formattedSession;
        });

        const ws = XLSX.utils.json_to_sheet(filteredData);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Accounting Sessions");
        XLSX.writeFile(
          wb,
          `Sessions_${new Date().toISOString().split("T")[0]}.xlsx`,
        );
        Swal.fire("Success", "Export Complete", "success");
      } else {
        Swal.fire("No Data", "No sessions found", "info");
      }
    } catch (error) {
      Swal.fire("Export Failed", "Error generating file", "error");
    }
  };

  return (
    <>
      <SearchSingleSessions
        setSearchReq={setSearchReq}
        GetTableData={GetTableData}
      />

      <Card style={{ marginTop: "70px" }}>
        <MDBox
          mt={-5}
          py={3}
          px={2}
          sx={{
            backgroundColor: "#3560AD",
            borderTopLeftRadius: "1.1rem",
            borderTopRightRadius: "1.1rem",
          }}
          bgColor="info"
          coloredShadow="info"
        >
          <div className="text-center">
            <MDTypography variant="h4" color="white">
              {
                translations.createAccountsessionSingleSessionSearchTableAccountingSessionstable
              }
            </MDTypography>
            <MDTypography className="h6" color="white">
              <strong>
                {
                  translations.createAccountsessionSingleSessionSearchTableTotalItems
                }
                : {TotalAccountingItems}
              </strong>
            </MDTypography>
          </div>

          {!IsSearching && (
            <div className="text-right">
              <MDBox className="search-button-container">
                {SortConfig.key !== null && (
                  <MDButton
                    onClick={ClearSort}
                    sx={{
                      backgroundColor: "#d90000ff",
                      "&:hover": { backgroundColor: "#f2160aff" },
                      marginRight: "10px",
                    }}
                    color="error"
                  >
                    Clear Sort
                  </MDButton>
                )}
                <MDButton
                  onClick={ExportExcelList}
                  sx={{
                    backgroundColor: "#333333",
                    "&:hover": {
                      backgroundColor: "#3560AD",
                      border: "1px solid white",
                    },
                  }}
                  color="dark"
                >
                  &nbsp;
                  {
                    translations.createAccountsessionSingleSessionSearchTableGenerateReport
                  }
                </MDButton>
              </MDBox>

              {CurrentUser?.userType === "Therapist" && (
                <MDButton
                  onClick={GetTherapistSessions}
                  variant="gradient"
                  color="light"
                >
                  <Icon sx={{ fontWeight: "bold" }}>search</Icon>
                  <MDTypography
                    component="span"
                    sx={{
                      backgroundColor: "#4682B4",
                      "&:hover": { backgroundColor: "#B0DFE5" },
                    }}
                    color="text"
                    fontWeight="medium"
                  >
                    {
                      translations.createAccountsessionSingleSessionSearchTableFindMySessions
                    }
                  </MDTypography>
                </MDButton>
              )}
            </div>
          )}
        </MDBox>
        <MDBox p={3}>
          {IsSearching ? (
            <div
              style={{
                display: "flex",
                padding: "50px",
                justifyContent: "center",
              }}
            >
              <CircularProgress color="info" size={40} thickness={5} />
            </div>
          ) : (
            <DataTable
              table={{ columns: TableColumns, rows: TableDataRows }}
              isSorted={false}
              entriesPerPage={false}
              showTotalEntries={false}
              noEndBorder
            />
          )}
          <div className="text-right mx-3 my-4">
            <Pagination
              count={PaginationPages}
              page={Page}
              color="primary"
              onChange={(e, num) => setPage(num)}
              className="float-right"
              disabled={IsSearching}
            />
          </div>
        </MDBox>
      </Card>

      {selectedSession && (
        <AccountingEntryPopup
          open={showPaymentPopup}
          onClose={() => setSelectedSession(null)}
          session={selectedSession}
          onSuccess={handleRefresh}
          onRefresh={handleRefresh}
        />
      )}
    </>
  );
};

export default SingleSessionsTable;
